import { useEffect, useRef } from 'react';
import { AVATAR_H, AVATAR_W, drawAvatar, type AvatarLook } from '../art/avatar';
import { drawFish, drawFurniture, furnitureSize } from '../art/items';
import { drawCrop } from '../art/crops';
import type { CropKey } from '@bb/shared';

interface ThumbItem {
  type: string;
  slot: string | null;
  render: Record<string, unknown>;
}

/**
 * Item picture. Wearables are shown on the player's own character ("try it on"),
 * furniture and fish are drawn on their own.
 */
export function Thumb({ item, look, size = 84 }: { item: ThumbItem; look?: AvatarLook; size?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const c = canvas.getContext('2d')!;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, size, size);
    if (item.slot && look) {
      const k = (size - 6) / AVATAR_H;
      c.translate((size - AVATAR_W * k) / 2, 3);
      c.scale(k, k);
      drawAvatar(c, { ...look, wear: { ...(look.wear ?? {}), [item.slot]: item.render } });
      return;
    }
    c.translate(size / 2, size / 2);
    if (item.type === 'crop' && typeof item.render.crop === 'string') {
      drawCrop(c, item.render.crop as CropKey, 0, 2, size * 0.9);
      return;
    }
    if (item.type === 'fish') {
      drawFish(c, (item.render.color as string) ?? '#9fb3c0', size / 70);
      return;
    }
    const { w, h } = furnitureSize(item.render);
    const k = Math.min(1, (size - 10) / Math.max(w, h));
    c.scale(k, k);
    drawFurniture(c, item.render);
  }, [item, look, size]);
  return <canvas ref={ref} style={{ width: size, height: size }} aria-hidden="true" />;
}
